import { createContext, useState, useMemo } from "react";
import { createTheme } from "@mui/material/styles";

// color design tokens export
export const tokens = (mode) => ({
  ...(mode === "dark"
    ? {
        grey: {
          100: "#e0e0e0",
          200: "#c2c2c2",
          300: "#a3a3a3",
          400: "#858585",
          500: "#666666",
          600: "#525252",
          700: "#3d3d3d",
          800: "#292929",
          900: "#141414",
        },
        primary: {
          100: "#e1d4fb",
          200: "#c3a9f7",
          300: "#a67df2",
          400: "#8852ee",
          500: "#6a27ea",
          600: "#551fbb",
          700: "#40178c",
          800: "#2a105e",
          900: "#15082f",
        },
        greenAccent: {
          100: "#dbf5ee",
          200: "#b7ebde",
          300: "#94e2cd",
          400: "#70d8bd",
          500: "#4cceac",
          600: "#3da58a",
          700: "#2e7c67",
          800: "#1e5245",
          900: "#0f2922",
        },
        redAccent: {
          100: "#f8dcdb",
          200: "#f1b9b7",
          300: "#e99592",
          400: "#e2726e",
          500: "#db4f4a",
          600: "#af3f3b",
          700: "#832f2c",
          800: "#58201e",
          900: "#2c100f",
        },
        blueAccent: {
          100: "#e1e2fe",
          200: "#c3c6fd",
          300: "#a4a9fc",
          400: "#1DA1F2", // manually added
          500: "#6870fa",
          600: "#535ac8",
          700: "#3e4396",
          800: "#2a2d64",
          900: "#151632",
        },
        whiteAccent: {
          100: "#fdfdfd",
          200: "#fbfbfb",
          300: "#f9f9f9",
          400: "#f7f7f7",
          500: "#f5f5f5",
          600: "#c4c4c4",
          700: "#939393",
          800: "#626262",
          900: "#15082f",
        },

        black: {
          100: "#343744",
          200: "#ffffff", // manually added
          300: "#ffffff", // manually added
          400: "#ffffff", // manually added
          500: "#ffffff", // manually added
          600: "#ffffff", // manually added
          700: "#ffffff", // manually added
          800: "#ffffff", // manually added
          900: "#ffffff", // manually added
        },
      }
    : {
        grey: {
          100: "#141414",
          200: "#292929",
          300: "#3d3d3d",
          400: "#525252",
          500: "#666666",
          600: "#858585",
          700: "#a3a3a3",
          800: "#c2c2c2",
          900: "#e0e0e0",
        },
        primary: {
          100: "#15082f",
          200: "#2a105e",
          300: "#40178c",
          400: "#551fbb",
          500: "#6a27ea",
          600: "#8852ee",
          700: "#a67df2",
          800: "#c3a9f7",
          900: "#F1EAFF",
        },
        greenAccent: {
          100: "#0f2922",
          200: "#1e5245",
          300: "#2e7c67",
          400: "#3da58a",
          500: "#4cceac",
          600: "#70d8bd",
          700: "#94e2cd",
          800: "#b7ebde",
          900: "#61BB84",
        },
        redAccent: {
          100: "#2c100f",
          200: "#58201e",
          300: "#832f2c",
          400: "#af3f3b",
          500: "#db4f4a",
          600: "#e2726e",
          700: "#e99592",
          800: "#f1b9b7",
          900: "#f8dcdb",
        },
        blueAccent: {
          100: "#151632",
          200: "#2a2d64",
          300: "#3e4396",
          400: "#1DA1F2", // manually added
          500: "#6870fa",
          600: "#868dfb",
          700: "#a4a9fc",
          800: "#c3c6fd",
          900: "#e1e2fe",
        },
        whiteAccent: {
          100: "#313131",
          200: "#626262",
          300: "#939393",
          400: "#c4c4c4",
          500: "#f5f5f5",
          600: "#f7f7f7",
          700: "#f9f9f9",
          800: "#fbfbfb",
          900: "#ffffff", // manually added
        },
        black: {
          100: "#ffffff",
          200: "#79879C", // manually added
          300: "#7C8DA7", // manually added
          400: "#141736", // manually added
          500: "#343744",
          600: "#5E636A", // manually added
          700: "#200E32", // manually added
          800: "#798BA3", // manually added
          900: "#15121D", // manually added
        },
      }),
});

// mui theme settings
export const themeSettings = (mode) => {
  const colors = tokens(mode);
  return {
    palette: {
      mode: mode,
      ...(mode === "dark"
        ? {
            // palette values for dark mode
            primary: {
              main: colors.primary[500],
              light: colors.primary[500],
            },
            secondary: {
              main: colors.whiteAccent[500],
              dark: colors.whiteAccent[900],
              light: colors.whiteAccent[100],
            },
            neutral: {
              dark: colors.grey[700],
              main: colors.grey[500],
              light: colors.grey[100],
            },
            background: {
              default: colors.primary[100],
            },
          }
        : {
            // palette values for light mode
            primary: {
              main: colors.primary[100],
              light: colors.primary[500],
            },
            secondary: {
              main: colors.whiteAccent[500],
              dark: colors.whiteAccent[900],
              light: colors.whiteAccent[100],
            },
            neutral: {
              dark: colors.grey[700],
              main: colors.grey[500],
              light: colors.grey[100],
            },
            background: {
              default: colors.whiteAccent[500],
            },
          }),
    },
    typography: {
      button: {
        textTransform: "none",
      },
      fontFamily: "Poppins",
      fontSize: 12,
      h1: {
        fontFamily: "Poppins",
        fontSize: 35,
      },
      h2: {
        fontFamily: "Poppins",
        fontSize: 30,
      },
      h3: {
        fontFamily: "Poppins",
        fontSize: 28,
      },
      h4: {
        fontFamily: "Poppins",
        fontSize: 27,
      },
      h5: {
        fontFamily: "Poppins",
        fontSize: 25,
      },
      h6: {
        fontFamily: "Poppins",
        fontSize: 22,
      },
      subtitle1: {
        fontFamily: "Poppins",
        fontSize: 21,
      },
      subtitle2: {
        fontFamily: "Poppins",
        fontSize: 20,
      },
      body1: {
        fontFamily: "Poppins",
        fontSize: 18,
      },
      body2: {
        fontFamily: "Poppins",
        fontSize: 17,
      },
      caption: {
        fontFamily: "Poppins",
        fontSize: 16,
      },
      overline: {
        fontFamily: "Poppins",
        fontSize: 15,
        textTransform: "none",
      },
      fs14: {
        fontFamily: "Poppins",
        fontSize: 14,
      },
      fs13: {
        fontFamily: "Poppins",
        fontSize: 13,
      },
      fs12: {
        fontFamily: "Poppins",
        fontSize: 12,
      },
      fs11: {
        fontFamily: "Poppins",
        fontSize: 11,
      },
      fs10: {
        fontFamily: "Poppins",
        fontSize: 10,
      },
      fs9: {
        fontFamily: "Poppins",
        fontSize: 9,
      },
    },
    breakpoints: {
      values: {
        xs: 0,
        sm: 600,
        md: 1430,
        lg: 1540,
        xl: 1746,
      },
    },
  };
};

// context for color mode
export const ColorModeContext = createContext({
  toggleColorMode: () => {},
});

export const useMode = () => {
  const [mode, setMode] = useState("light");

  const colorMode = useMemo(
    () => ({
      toggleColorMode: () =>
        setMode((prev) => (prev === "light" ? "dark" : "light")),
    }),
    []
  );

  const theme = useMemo(() => createTheme(themeSettings(mode)), [mode]);
  return [theme, colorMode];
};
